import {useCallback, useMemo, useState} from 'react'
import {ScrollView, View} from 'react-native'
import {Image} from 'expo-image'
import {AppBskyEmbedVideo} from '@atproto/api'
import {plural} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {gradeLabel, subjectLabel} from '#/lib/curriculum'
import {type NavigationProp} from '#/lib/routes/types'
import {niceDate} from '#/lib/strings/time'
import {
  canPostInClassroom,
  type Classroom,
  libraryEntryVisibility,
  useClassroomActor,
} from '#/state/classrooms'
import {useActorCapsulesQuery} from '#/state/queries/capsule'
import {
  useClassroomPostViews,
  usePostViewsByUri,
} from '#/state/queries/classroom-posts'
import {
  useAddClassroomContentMutation,
  useClassroomContentQuery,
} from '#/state/queries/classrooms'
import {useSession} from '#/state/session'
import {atoms as a, useBreakpoints, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {type DialogControlProps} from '#/components/Dialog'
import * as TextField from '#/components/forms/TextField'
import {Check_Stroke2_Corner0_Rounded as CheckIcon} from '#/components/icons/Check'
import {Loader} from '#/components/Loader'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {showClassroomError} from './showClassroomError'

/**
 * Putting a capsule into a classroom.
 *
 * A capsule is not written here. Sharing one means pointing the classroom at a
 * capsule the account has already published: nothing is copied and nothing is
 * re-published, so the room shows the same post, which is why a capsule in a
 * classroom can be played, commented on and reacted to like anything else. It is
 * not a duplicate of the one on the profile — it *is* that one.
 *
 * A capsule made *for* the room is the other half of the story and does not
 * arrive through here: it is made in the studio, opened from this room, and it is
 * published as a post that belongs to the classroom alone — stamped so the app
 * keeps it out of profiles and feeds while the room still reads, comments on and
 * reacts to it like any other post (see `isClassroomExclusive`). This dialog is
 * about the first kind — the ones already published, and therefore already
 * playable by anyone.
 *
 * Which is why this is a *library* rather than a list of buttons. A teacher with
 * a year of work has dozens of capsules, so the row of identical rectangles it
 * used to be answered no question a teacher actually has. What they have is a
 * question — the one from last year, for the fifth year, in Maths — so the
 * picker is a search and two filters over a grid of small cards, each card
 * saying what the capsule is, who it is for, when it was made, and how much work
 * is in it.
 *
 * Posts are not written here at all. A post for a classroom is written in the
 * composer, and goes into the room by being posted.
 */
export function AddCapsuleDialog({
  control,
  classroom,
}: {
  control: DialogControlProps
  classroom: Classroom
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  const actor = useClassroomActor()
  const navigation = useNavigation<NavigationProp>()
  const {hasSession} = useSession()
  const {gtMobile} = useBreakpoints()

  const canPost = canPostInClassroom(classroom, actor)
  const [selectedUri, setSelectedUri] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [levelFilter, setLevelFilter] = useState<string | null>(null)
  const [subjectFilter, setSubjectFilter] = useState<string | null>(null)
  const [oldestFirst, setOldestFirst] = useState(false)
  /**
   * Bumped to clear the search box.
   *
   * The form fields here are `defaultValue` and not `value` — the codebase
   * deprecates controlled inputs — so a box the app empties has to be *made*
   * again rather than told. Clearing the filters without this left the search
   * text on screen while the grid behind it had already forgotten it, which
   * reads as a filter that does not work.
   */
  const [searchGeneration, setSearchGeneration] = useState(0)

  /*
   * The room's year, when it has one.
   *
   * A classroom pinned to a year holds the work of that year and nothing else —
   * a 2nd-year room is not a place for a 5th-year capsule — so the library is
   * read through the same pin the room itself carries. The room's year and the
   * work's year are one vocabulary (`CLASSROOM_LEVEL_GRADES`), which is what
   * makes the filter a comparison rather than a translation.
   */
  const pinnedGrade = classroom.grade ? gradeLabel(classroom.grade) : null
  const pinnedGradeText = pinnedGrade ? l(pinnedGrade) : (classroom.grade ?? '')

  const {data: inRoomPages} = useClassroomContentQuery(classroom.id, 'capsule')
  const inRoom = inRoomPages?.pages.flatMap(page => page.content) ?? []
  const addContent = useAddClassroomContentMutation(classroom.id)
  /**
   * The capsules this room already holds.
   *
   * A shared capsule is a pointer at a post, and a post is in a room once (see
   * `addContent`), so one that is already here can only be a card that
   * does nothing. It stays in the grid and says so rather than disappearing —
   * a library that quietly hides a third of itself is a library somebody will
   * search twice for the same missing capsule.
   */
  const alreadyHere = useMemo(
    () => new Set(inRoom.map(entry => entry.postUri).filter(Boolean)),
    [inRoom],
  )

  const {data: records, isPending} = useActorCapsulesQuery(
    canPost ? actor?.did : undefined,
  )

  /**
   * Everything this account has published, newest first.
   *
   * Read from the capsule records rather than from the author's posts. A post
   * carries a back-reference to its capsule and none of the capsule's own
   * fields, so a picker built from posts can show a title and nothing else, and
   * has no way to filter by level or subject at all. A record contains the lot.
   *
   * Two kinds of capsule are left out on purpose, and they are the same kind of
   * thing said two ways. One with no `post` was never published, so there is no
   * post to point this classroom at. One marked `visibility: 'classroom'` *is* a
   * post now — that is what lets the room it was written for comment on it and
   * react to it — but it belongs to that room, and a capsule made for one
   * classroom is not a thing another classroom can be sent.
   */
  const library = useMemo<CapsuleChoice[]>(() => {
    const rows: CapsuleChoice[] = []
    for (const record of records ?? []) {
      if (!record.postUri || record.status === 'draft') continue
      if (record.visibility === 'classroom') continue
      if (classroom.grade && record.level !== classroom.grade) continue
      rows.push({
        visibility: record.visibility,
        postUri: record.postUri,
        title: record.title.trim() || firstLine(record.description),
        description: record.description,
        level: record.level,
        subject: record.subject,
        slideCount: record.slideCount,
        createdAt: record.createdAt,
      })
    }
    return rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }, [classroom.grade, records])

  /**
   * The cover a published capsule's post carries.
   *
   * The film itself lives on the post, not on the capsule record, so a library
   * drawn from records has to ask the posts for their thumbnails or it renders a
   * wall of empty tiles. Batched, like every other read of a set of posts, so a
   * large library is a couple of requests rather than one per card.
   */
  const postUris = useMemo(
    () => library.map(choice => choice.postUri),
    [library],
  )
  const {data: postViews} = useClassroomPostViews(postUris)
  const viewsByUri = usePostViewsByUri(postViews)
  const thumbnailFor = useCallback(
    (postUri: string): string | undefined => {
      const embed = viewsByUri.get(postUri)?.embed
      return embed && AppBskyEmbedVideo.isView(embed)
        ? embed.thumbnail
        : undefined
    },
    [viewsByUri],
  )

  /**
   * Capsules whose post is not there any more.
   *
   * A record outlives its post: deleting the post leaves the capsule behind,
   * pointing at an address with nothing at it, and the library is built from
   * records — so without this the picker offers work that arrives in the room as
   * *This capsule is no longer here.*, which is a row nobody can play and nobody
   * asked for. Only said once the posts have actually answered: a post that has
   * not come back *yet* is not the same as one that is gone.
   */
  const unavailable = useMemo(() => {
    const missing = new Set<string>()
    if (!postViews) return missing
    const here = new Set(postViews.map(post => post.uri))
    for (const choice of library) {
      if (!here.has(choice.postUri)) missing.add(choice.postUri)
    }
    return missing
  }, [library, postViews])

  /** Only the filters this library can answer with: no empty chip rows. */
  const levels = useMemo(
    () =>
      Array.from(
        new Set(
          library.map(choice => choice.level).filter((v): v is string => !!v),
        ),
      ).sort(),
    [library],
  )
  const subjects = useMemo(
    () =>
      Array.from(
        new Set(
          library.map(choice => choice.subject).filter((v): v is string => !!v),
        ),
      ).sort(),
    [library],
  )

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const filtered = library.filter(choice => {
      if (levelFilter && choice.level !== levelFilter) return false
      if (subjectFilter && choice.subject !== subjectFilter) return false
      if (
        needle &&
        !`${choice.title} ${choice.description}`.toLowerCase().includes(needle)
      ) {
        return false
      }
      return true
    })
    return oldestFirst ? [...filtered].reverse() : filtered
  }, [levelFilter, library, oldestFirst, query, subjectFilter])

  const clearFilters = useCallback(() => {
    setQuery('')
    setLevelFilter(null)
    setSubjectFilter(null)
    setSearchGeneration(generation => generation + 1)
  }, [])

  const selected = useMemo(
    () => library.find(choice => choice.postUri === selectedUri) ?? null,
    [library, selectedUri],
  )

  const onAdd = useCallback(() => {
    if (!actor || !selected) return
    addContent.mutate(
      {
        classroomId: classroom.id,
        kind: 'capsule',
        // Published work is public in the room unless it is classroom-only.
        visibility: libraryEntryVisibility(selected),
        text: selected.description,
        capsuleTitle: selected.title,
        postUri: selected.postUri,
      },
      {
        onSuccess: () => Toast.show(l`Capsule added to ${classroom.name}`),
        onError: error => showClassroomError(i18n, error),
      },
    )
    setSelectedUri(null)
    control.close()
  }, [
    actor,
    addContent,
    classroom.id,
    classroom.name,
    control,
    i18n,
    l,
    selected,
  ])

  const onOpenStudio = useCallback(() => {
    setSelectedUri(null)
    control.close()
    // The classroom travels with the studio, so publishing there puts the
    // capsule in this room and comes back to it.
    navigation.navigate('CapsuleStudio', {classroomId: classroom.id})
  }, [classroom.id, control, navigation])

  if (!hasSession || !canPost) {
    return (
      <Dialog.Outer control={control}>
        <Dialog.Handle />
        <Dialog.ScrollableInner label={l`Add a capsule to this classroom`}>
          <View style={[a.gap_lg]}>
            <Text style={[a.text_xl, a.font_bold]}>
              {l`Adding is for whoever runs this classroom and its teachers`}
            </Text>
            <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
              {l`The account that runs this classroom, and the teachers in it, put the work here. Students and parents read it.`}
            </Text>
          </View>
          <Dialog.Close />
        </Dialog.ScrollableInner>
      </Dialog.Outer>
    )
  }

  const loading = isPending && library.length === 0

  return (
    <Dialog.Outer control={control} onClose={() => setSelectedUri(null)}>
      <Dialog.Handle />
      <Dialog.ScrollableInner
        label={l`Add a capsule to ${classroom.name}`}
        style={[a.w_full, {maxWidth: 640}]}>
        <View style={[a.gap_md]}>
          <View style={[a.gap_xs]}>
            <Text style={[a.text_xl, a.font_bold]}>
              {l`Add a capsule to ${classroom.name}`}
            </Text>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              {l`Your published capsules. Adding one shares it with this classroom — it stays on your profile, and anyone can play it.`}
            </Text>
            {classroom.grade ? (
              <Text
                testID="classroomCapsuleLevelNote"
                style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {l`This classroom is set at ${pinnedGradeText} — only your ${pinnedGradeText} capsules can be added to it.`}
              </Text>
            ) : null}
          </View>

          {loading ? (
            <View style={[a.flex_row, a.align_center, a.gap_sm, a.py_2xl]}>
              <Loader size="sm" />
              <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
                {l`Looking for your capsules…`}
              </Text>
            </View>
          ) : library.length === 0 ? (
            <NothingPublished onOpenStudio={onOpenStudio} />
          ) : (
            <>
              <Filters
                searchGeneration={searchGeneration}
                query={query}
                onQuery={setQuery}
                levels={levels}
                level={levelFilter}
                onLevel={setLevelFilter}
                subjects={subjects}
                subject={subjectFilter}
                onSubject={setSubjectFilter}
                oldestFirst={oldestFirst}
                onOldestFirst={setOldestFirst}
                shown={results.length}
                total={library.length}
              />

              {results.length === 0 ? (
                <View
                  testID="classroomCapsuleFilterEmpty"
                  style={[
                    a.gap_md,
                    a.p_lg,
                    a.rounded_md,
                    a.align_center,
                    t.atoms.bg_contrast_25,
                  ]}>
                  <Text style={[a.text_md, a.font_semi_bold, a.text_center]}>
                    {l`No capsule matches that.`}
                  </Text>
                  <Text
                    style={[
                      a.text_sm,
                      a.text_center,
                      t.atoms.text_contrast_medium,
                    ]}>
                    {l`Your library is still here — these filters are just narrower than it is.`}
                  </Text>
                  <Button
                    testID="classroomCapsuleClearFilters"
                    label={l`Clear the filters`}
                    color="secondary"
                    size="small"
                    onPress={clearFilters}>
                    <ButtonText>{l`Clear filters`}</ButtonText>
                  </Button>
                </View>
              ) : (
                <CapsuleGrid
                  choices={results}
                  columns={gtMobile ? 3 : 2}
                  selectedUri={selectedUri}
                  alreadyHere={alreadyHere}
                  unavailable={unavailable}
                  thumbnailFor={thumbnailFor}
                  onSelect={uri =>
                    setSelectedUri(current => (current === uri ? null : uri))
                  }
                />
              )}

              <View style={[a.gap_sm, a.pt_xs]}>
                <Button
                  testID="classroomCapsuleSubmit"
                  label={l`Add this capsule to the classroom`}
                  size="large"
                  color="primary"
                  disabled={!selected}
                  onPress={onAdd}>
                  <ButtonText>{l`Add to this classroom`}</ButtonText>
                </Button>
                <Button
                  testID="classroomMakeCapsule"
                  label={l`Make a new capsule for this classroom`}
                  color="secondary"
                  size="small"
                  style={[a.self_start]}
                  onPress={onOpenStudio}>
                  <ButtonText>{l`Make a new capsule`}</ButtonText>
                </Button>
              </View>
            </>
          )}
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}

/** One capsule that can be shared into the classroom. */
interface CapsuleChoice {
  /** The record's own visibility (`classroom` for classroom-only work). */
  visibility?: string
  /** The post it was published as — what the classroom will point at. */
  postUri: string
  title: string
  description: string
  /** The grade code the publisher chose (`Level_A5`), not a name. */
  level?: string
  subject?: string
  slideCount?: number
  createdAt: string
}

/**
 * The search box, the two filters and the order.
 *
 * Only what the library can answer with appears: a level nobody has published at
 * is not a chip, because a filter that can only ever empty the grid is a filter
 * that teaches nothing. The count beside the order is the other half of that —
 * it says how much of the library the current filters are showing, so a teacher
 * who has narrowed too far can see that they have.
 */
function Filters({
  searchGeneration,
  query,
  onQuery,
  levels,
  level,
  onLevel,
  subjects,
  subject,
  onSubject,
  oldestFirst,
  onOldestFirst,
  shown,
  total,
}: {
  /** Changes when the app clears the box; see the state that owns it. */
  searchGeneration: number
  query: string
  onQuery: (value: string) => void
  levels: string[]
  level: string | null
  onLevel: (value: string | null) => void
  subjects: string[]
  subject: string | null
  onSubject: (value: string | null) => void
  oldestFirst: boolean
  onOldestFirst: (value: boolean) => void
  shown: number
  total: number
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  return (
    <View testID="classroomCapsulePicker" style={[a.gap_sm]}>
      <TextField.Root>
        <TextField.Input
          key={`classroomCapsuleSearch-${searchGeneration}`}
          testID="classroomCapsuleSearch"
          label={l`Search your capsules`}
          placeholder={l`Search your capsules`}
          defaultValue={query}
          onChangeText={onQuery}
        />
      </TextField.Root>

      {levels.length > 1 ? (
        <ChipRow
          label={l`Year`}
          allLabel={l`All years`}
          testIDPrefix="classroomCapsuleLevel"
          options={levels.map(value => ({
            value,
            // A record stores `Level_A5`; a teacher reads "5th year".
            label: l(gradeLabel(value) ?? {message: value}),
          }))}
          selected={level}
          onSelect={onLevel}
        />
      ) : null}

      {subjects.length > 1 ? (
        <ChipRow
          label={l`Subject`}
          allLabel={l`All subjects`}
          testIDPrefix="classroomCapsuleSubject"
          options={subjects.map(value => ({
            value,
            label: l(subjectLabel(value) ?? {message: value}),
          }))}
          selected={subject}
          onSelect={onSubject}
        />
      ) : null}

      <View style={[a.flex_row, a.align_center, a.gap_sm]}>
        <Text style={[a.flex_1, a.text_sm, t.atoms.text_contrast_medium]}>
          {shown === total
            ? plural(total, {one: '# capsule', other: '# capsules'})
            : l`${shown} of ${total} capsules`}
        </Text>
        <Button
          testID="classroomCapsuleSort"
          label={
            oldestFirst ? l`Oldest capsules first` : l`Newest capsules first`
          }
          color="secondary"
          size="tiny"
          onPress={() => onOldestFirst(!oldestFirst)}>
          <ButtonText>
            {oldestFirst ? l`Oldest first` : l`Newest first`}
          </ButtonText>
        </Button>
      </View>
    </View>
  )
}

/**
 * One filter, as a strip of chips.
 *
 * A strip rather than a wrapped block: a year list and a subject list together
 * are taller than the grid they are filtering when they wrap, and a chooser that
 * pushes its own results off the screen is not a chooser. It scrolls sideways
 * instead, which is what a strip of chips on a phone has always meant.
 */
function ChipRow({
  label,
  allLabel,
  testIDPrefix,
  options,
  selected,
  onSelect,
}: {
  label: string
  allLabel: string
  testIDPrefix: string
  options: {value: string; label: string}[]
  selected: string | null
  onSelect: (value: string | null) => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  return (
    <View style={[a.gap_xs]}>
      <Text style={[a.text_xs, a.font_semi_bold, t.atoms.text_contrast_medium]}>
        {label}
      </Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[a.flex_row, a.gap_xs]}>
        <Chip
          testID={`${testIDPrefix}-all`}
          label={allLabel}
          selected={!selected}
          onPress={() => onSelect(null)}
        />
        {options.map(option => (
          <Chip
            key={option.value}
            testID={`${testIDPrefix}-${option.value}`}
            label={option.label}
            selected={selected === option.value}
            onPress={() =>
              onSelect(selected === option.value ? null : option.value)
            }
          />
        ))}
      </ScrollView>
    </View>
  )
}

function Chip({
  label,
  selected,
  onPress,
  testID,
}: {
  label: string
  selected: boolean
  onPress: () => void
  testID: string
}) {
  return (
    <Button
      testID={testID}
      label={label}
      size="tiny"
      color={selected ? 'primary' : 'secondary'}
      shape="default"
      onPress={onPress}>
      <ButtonText>{label}</ButtonText>
    </Button>
  )
}

/**
 * The library itself: small cards, as many as fit.
 *
 * The columns are decided here and the rows are laid out by hand, because a grid
 * in a scrolling dialog is not a grid in a list: `Grid.Row` is built to fill the
 * height of a list row it was handed, and inside a plain column that height is a
 * question nobody has answered. Flex columns keep every card the same width and
 * an equal share of the row, and the last row is padded so its single card is
 * the size of a card and not the width of the dialog.
 */
function CapsuleGrid({
  choices,
  columns,
  selectedUri,
  alreadyHere,
  unavailable,
  thumbnailFor,
  onSelect,
}: {
  choices: CapsuleChoice[]
  columns: number
  selectedUri: string | null
  alreadyHere: Set<string | undefined>
  /** Capsules whose post no longer exists; see the set's own note. */
  unavailable: Set<string>
  thumbnailFor: (postUri: string) => string | undefined
  onSelect: (postUri: string) => void
}) {
  const rows = useMemo(() => {
    const out: CapsuleChoice[][] = []
    for (let i = 0; i < choices.length; i += columns) {
      out.push(choices.slice(i, i + columns))
    }
    return out
  }, [choices, columns])

  return (
    <ScrollView
      testID="classroomCapsuleGrid"
      style={[{maxHeight: 340}]}
      contentContainerStyle={[a.gap_sm]}>
      {rows.map(row => (
        <View
          key={row.map(choice => choice.postUri).join(' ')}
          style={[a.flex_row, a.gap_sm, a.align_stretch]}>
          {row.map(choice => (
            <View key={choice.postUri} style={[a.flex_1]}>
              <CapsuleChoiceCard
                choice={choice}
                selected={selectedUri === choice.postUri}
                alreadyHere={alreadyHere.has(choice.postUri)}
                unavailable={unavailable.has(choice.postUri)}
                thumbnail={thumbnailFor(choice.postUri)}
                onPress={() => onSelect(choice.postUri)}
              />
            </View>
          ))}
          {Array.from({length: columns - row.length}).map((_, i) => (
            <View key={`filler-${i}`} style={[a.flex_1]} />
          ))}
        </View>
      ))}
    </ScrollView>
  )
}

/**
 * One capsule, as a card.
 *
 * The cover when the post has one and its level, its subject and the month it
 * was made underneath — which is the set of facts a teacher filters by, so it is
 * the set of facts the card has to carry. A capsule with no cover film gets the
 * tile and the same words rather than a hole, because a cover is a nicety and
 * the title is not.
 */
function CapsuleChoiceCard({
  choice,
  selected,
  alreadyHere,
  unavailable,
  thumbnail,
  onPress,
}: {
  choice: CapsuleChoice
  selected: boolean
  alreadyHere: boolean
  unavailable: boolean
  thumbnail?: string
  onPress: () => void
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  /** Nothing to add, for one of two reasons — and each has its own word. */
  const blocked = alreadyHere || unavailable

  const level = choice.level ? gradeLabel(choice.level) : null
  const subject = choice.subject ? subjectLabel(choice.subject) : null
  const meta = [
    level ? l(level) : null,
    subject ? l(subject) : null,
    choice.createdAt
      ? niceDate(i18n, choice.createdAt, 'medium', 'none')
      : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Button
      testID={`classroomCapsuleOption-${choice.postUri}`}
      label={l`Add ${choice.title}`}
      disabled={blocked}
      onPress={onPress}
      style={[a.w_full, a.flex_1]}>
      {({hovered, pressed}) => (
        <View
          style={[
            a.flex_1,
            a.rounded_lg,
            a.border,
            {
              padding: 2,
              borderWidth: 2,
              borderColor: selected ? t.palette.primary_500 : 'transparent',
              opacity: blocked ? 0.55 : 1,
            },
          ]}>
          <View
            style={[
              a.flex_1,
              a.rounded_md,
              a.overflow_hidden,
              a.border,
              t.atoms.border_contrast_low,
              t.atoms.bg_contrast_25,
              (hovered || pressed) && !blocked
                ? t.atoms.border_contrast_high
                : null,
            ]}>
            <View style={[{aspectRatio: 4 / 3}]}>
              {thumbnail ? (
                <Image
                  source={{uri: thumbnail}}
                  style={[a.w_full, a.h_full]}
                  contentFit="cover"
                  accessibilityIgnoresInvertColors
                />
              ) : (
                /*
                 * A capsule with no cover film is not an empty tile: the film is
                 * the *post's*, and a capsule published without one is normal. What
                 * the card has instead is the thing being chosen — the title — and
                 * an empty rectangle would make a third of a library look broken.
                 */
                <View
                  style={[
                    a.w_full,
                    a.h_full,
                    a.justify_center,
                    a.align_center,
                    a.p_sm,
                    t.atoms.bg_contrast_50,
                  ]}>
                  <Text
                    emoji
                    numberOfLines={3}
                    style={[
                      a.text_sm,
                      a.font_bold,
                      a.text_center,
                      t.atoms.text_contrast_high,
                    ]}>
                    {choice.title || l`Untitled capsule`}
                  </Text>
                </View>
              )}
              {typeof choice.slideCount === 'number' &&
              choice.slideCount > 0 ? (
                <View
                  style={[
                    a.absolute,
                    {
                      top: 6,
                      left: 6,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 3,
                      backgroundColor: 'rgba(0,0,0,0.55)',
                      borderRadius: 20,
                      paddingHorizontal: 8,
                      paddingVertical: 3,
                    },
                  ]}>
                  <Text style={[a.text_xs]}>🪙</Text>
                  <Text
                    style={[
                      a.text_xs,
                      a.font_bold,
                      {color: '#FFD700', lineHeight: 14},
                    ]}>
                    {choice.slideCount}
                  </Text>
                </View>
              ) : null}
              {blocked ? (
                /*
                 * Said as a chip on the picture rather than across it. The card
                 * is already dimmed and does not respond, so the words only have
                 * to name the reason — a sentence down the middle of every tile
                 * in a row the teacher has already filled buries the covers they
                 * are scanning for.
                 */
                <View
                  testID={
                    alreadyHere
                      ? `classroomCapsuleInRoom-${choice.postUri}`
                      : `classroomCapsuleGonePick-${choice.postUri}`
                  }
                  style={[a.absolute, {bottom: 6, left: 6}]}>
                  <View
                    style={[
                      a.rounded_full,
                      a.px_sm,
                      {
                        paddingVertical: 3,
                        backgroundColor: 'rgba(0,0,0,0.65)',
                      },
                    ]}>
                    <Text
                      style={[
                        a.text_xs,
                        a.font_semi_bold,
                        {color: 'white', lineHeight: 14},
                      ]}>
                      {alreadyHere
                        ? l`In this classroom`
                        : l`No longer available`}
                    </Text>
                  </View>
                </View>
              ) : selected ? (
                <View
                  style={[
                    a.absolute,
                    {
                      top: 6,
                      right: 6,
                      width: 24,
                      height: 24,
                      borderRadius: 12,
                      backgroundColor: t.palette.primary_500,
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                  ]}>
                  <CheckIcon size="sm" fill="white" />
                </View>
              ) : null}
            </View>

            <View style={[a.p_xs, a.gap_2xs, {paddingBottom: 8}]}>
              <Text
                emoji
                numberOfLines={2}
                style={[a.text_sm, a.font_semi_bold, a.leading_tight]}>
                {choice.title || l`Untitled capsule`}
              </Text>
              <Text
                numberOfLines={2}
                style={[
                  a.text_2xs,
                  a.leading_tight,
                  t.atoms.text_contrast_medium,
                ]}>
                {meta || l`No year or subject`}
              </Text>
            </View>
          </View>
        </View>
      )}
    </Button>
  )
}

/**
 * An account with nothing published.
 *
 * Two situations wear the same words because they are the same problem from the
 * teacher's side — nothing here can be added — and the same way out: a capsule
 * made in the studio. If it is published from the studio while a classroom is
 * open, it lands in that classroom by itself; if not, it comes back here as a
 * card to share.
 */
function NothingPublished({onOpenStudio}: {onOpenStudio: () => void}) {
  const t = useTheme()
  const {t: l} = useLingui()

  return (
    <View
      testID="classroomCapsulePickerEmpty"
      style={[a.gap_md, a.p_lg, a.rounded_md, t.atoms.bg_contrast_25]}>
      <Text style={[a.text_md, a.font_semi_bold]}>
        {l`You have no published capsules to share yet`}
      </Text>
      <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
        {l`A capsule is made in the studio. Publishing it puts it on your profile, where anyone can play it and where this room can be pointed at it — and a capsule you publish from inside a classroom stays between you and that classroom instead.`}
      </Text>
      <Button
        testID="classroomOpenStudio"
        label={l`Open the capsule studio`}
        color="primary"
        size="small"
        style={[a.self_start]}
        onPress={onOpenStudio}>
        <ButtonText>{l`Open the studio`}</ButtonText>
      </Button>
    </View>
  )
}

/** The first line of a body of text, as a card's title. */
function firstLine(text: string): string {
  const [line] = text.trim().split('\n')
  return line?.trim() ?? ''
}
